import { useParams } from "react-router-dom";
import { Check, SearchX, X } from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DetailList, DetailRow } from "@/components/detail-list";
import { DevDeviceBadge } from "@/components/dev-device-badge";
import { EmptyState } from "@/components/empty-state";
import { DetailSkeleton } from "@/components/loading-skeletons";
import { PageHeader, PageTitle } from "@/components/layout/page-header";
import { useDeviceQuery } from "@/hooks/use-devices";
import { formatDeviceModel } from "@/lib/utils";
import { getPlatformLabel, PlatformIcon } from "@/lib/device-platform";

const BREADCRUMBS = [{ label: "Devices", to: "/devices" }];

export function DeviceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: device, isLoading, error, refetch } = useDeviceQuery(id!);

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-6">
        <PageHeader breadcrumbs={BREADCRUMBS} title="Device" />
        <DetailSkeleton />
      </div>
    );
  }

  if (error || !device) {
    return (
      <div className="flex flex-1 flex-col gap-6">
        <PageHeader breadcrumbs={BREADCRUMBS} title="Device" />
        <EmptyState
          icon={SearchX}
          title="Device Not Found"
          description="The device you are looking for does not exist."
        >
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6">
      <PageHeader breadcrumbs={BREADCRUMBS} title={device.name} />

      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
        <PageTitle
          icon={
            <PlatformIcon
              platform={device.platform}
              className="size-7 shrink-0 text-muted-foreground"
            />
          }
          title={
            <span className="inline-flex items-center gap-2.5">
              {device.name}
              <DevDeviceBadge deviceId={device.id} />
            </span>
          }
          description={
            device.marketing_name ||
            (device.model && formatDeviceModel(device.model))
          }
        />

        <Card>
          <CardHeader>
            <CardTitle>Device Details</CardTitle>
          </CardHeader>
          <CardContent>
            <DetailList>
              <DetailRow label="Platform">
                {getPlatformLabel(device.platform)}
              </DetailRow>
              {device.os_version && (
                <DetailRow label="OS Version">{device.os_version}</DetailRow>
              )}
              {device.model && (
                <DetailRow label="Model">{formatDeviceModel(device.model)}</DetailRow>
              )}
            </DetailList>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Device Metadata</CardTitle>
          </CardHeader>
          <CardContent>
            <DetailList>
              <DetailRow label="Notifications">
                {device.notifications_enabled ? (
                  <Badge variant="success">
                    <Check aria-hidden="true" />
                    Enabled
                  </Badge>
                ) : (
                  <Badge variant="error">
                    <X aria-hidden="true" />
                    Disabled
                  </Badge>
                )}
              </DetailRow>
              <DetailRow label="User ID">{device.user_id}</DetailRow>
            </DetailList>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
